import { useCallback, useEffect, useState } from 'react'
import usePodcastFormStepsStore from 'store/usePodcastFormStepsStore'
import { useAuth } from 'util/auth'
import { Upload } from './Upload'
import { SpeakerInput } from './SpeakerInput'
import { Button } from './Button'
import { SubmitSummary } from './SubmitSummary'
import { requestTranscription } from 'util/requestTranscription'
import { useRouter } from 'next/router'
import ModalConfirm from './ModalConfirm'
import { UploadFormHeader } from './UploadFormHeader'
import { LanguageSelect } from './LanguageSelect'
import { ShowNameCombobox } from './ShowNameCombobox'
import AlertMessage from './AlertMessage'
import { UploadMethod } from 'store/useStore'

const hasUploadIssue = false

export const UploadForm = () => {
  const {
    submission,
    updateSubmission,
    podcast,
    updatePodcast,
    setOwner,
    currentStepIndex,
    setCurrentStepIndex,
    resetState,
  } = usePodcastFormStepsStore()

  const auth = useAuth()
  const router = useRouter()

  useEffect(() => {
    if (auth.user) {
      setOwner(auth.user.id)
    }
  }, [auth.user])

  const [showName, setShowName] = useState('')
  const [audioStartFrom, setAudioStartFrom] = useState<undefined | number>()
  const [audioStartFromInput, setAudioStartFromInput] = useState<string>()
  const [audioEndAt, setAudioEndAt] = useState<undefined | number>()
  const [audioEndAtInput, setAudioEndAtInput] = useState<string>()
  const [type, setType] = useState<UploadMethod>('upload')

  const handleReview = () => {
    setCurrentStepIndex(currentStepIndex + 1)
    updatePodcast({
      ...podcast,
      show_name: showName,
    })
  }

  const handleGoBack = () => {
    setCurrentStepIndex(currentStepIndex - 1)
    updatePodcast({
      ...podcast,
      show_name: showName,
    })
  }

  const [confirmTranscriptionRequest, setConfirmTranscriptionRequest] =
    useState(false)
  const [transcriptionError, setTranscriptionError] = useState<any>(null)

  const handleConfirmTranscriptionRequest = async () => {
    if (!submission || !podcast) {
      return
    }

    setConfirmTranscriptionRequest(false)

    // make sure all fields are filled out
    let hasError = false
    if (!podcast.show_name) {
      hasError = true
      setTranscriptionError('Please fill out your show name')
    } else if (
      !podcast.speakers ||
      (podcast.speakers as string[]).length === 0
    ) {
      hasError = true
      setTranscriptionError('Please fill out your speakers')
    }

    if (hasError) {
      return
    }

    const updatedPodcast = await updatePodcast(podcast)
    // remove the s3_file_key from the submission object
    const submissionCopy = { ...submission }
    delete submissionCopy.s3_file_key

    const updatedSubmission = await updateSubmission({
      ...submissionCopy,
      podcast_id: updatedPodcast?.id,
      status: 'requesting transcription',
      upload_method: type,
    })

    const transcriptionRequest = await requestTranscription(
      updatedSubmission,
      auth.user ? auth.user.id : '',
      type
    )

    if (transcriptionRequest.error) {
      // check if the error string contains the text word_boost
      if (
        transcriptionRequest.error &&
        transcriptionRequest.error.includes('word_boost')
      ) {
        setTranscriptionError(
          "Please make sure that Speaker labels don't contain any digits. Instead, opt to use letters like 'A', 'B', 'C', etc."
        )
      } else {
        setTranscriptionError(transcriptionRequest.description)
      }
      await updateSubmission({
        ...updatedSubmission,
        status: 'pending',
      })
    } else {
      router.push('/dashboard')
      resetState()
    }
  }

  const handleSubmit = () => {
    setConfirmTranscriptionRequest(true)
  }

  const getStep = () => {
    switch (currentStepIndex) {
      case 0:
        return (
          <Upload
            onUpload={({ type }) => {
              setCurrentStepIndex(currentStepIndex + 1)
              setType(type)
            }}
          />
        )
      case 1:
        return (
          <>
            <h2 className="mb-4 text-2xl font-bold">{submission.filename}</h2>
            <div className="mb-4">
              <p className="mb-4 text-gray-500">
                {
                  "1. Let's break the ice! Tell us your podcast shows name or YouTube channel name."
                }
              </p>
              <ShowNameCombobox
                showName={showName}
                setShowName={setShowName}
                submissionId={submission?.id}
              />
            </div>
            <div className="my-8 border-t border-gray-200"></div>
            <div className="mb-4">
              <p className="mb-4 text-gray-500">
                2. What language is this episode in?
              </p>
              <LanguageSelect
                onSelectChange={({ language, language_code }) =>
                  updateSubmission({
                    ...submission,
                    language_code,
                    language,
                  })
                }
              />
            </div>
            <div className="my-8 border-t border-gray-200"></div>

            <div className="mb-4">
              <p className="mb-4 text-gray-500">
                {
                  "3. Who's speaking in this episode? Make sure to enter each speakers name in the order that they appear on the episode."
                }
              </p>
              <SpeakerInput />
            </div>
            <div className="my-8 border-t border-gray-200"></div>
            <div className="mt-4 flex justify-between">
              <Button variant="outline" color="blue" onClick={handleGoBack}>
                &larr; Upload a different file
              </Button>
              <Button onClick={handleReview}>
                Review Your Episode Details &rarr;
              </Button>
            </div>
          </>
        )
      default:
        return (
          <>
            {transcriptionError && (
              <ModalConfirm
                message={JSON.stringify(transcriptionError)}
                onConfirm={() => setTranscriptionError(null)}
                title="Whoops!"
                type="error"
                handleClose={() => setTranscriptionError(null)}
              />
            )}
            {confirmTranscriptionRequest && (
              <ModalConfirm
                message="Are you sure you want to request a transcription? This will use one of your submission credits."
                onConfirm={handleConfirmTranscriptionRequest}
                title="Confirm transcription request"
                leftButtonLabel="Cancel"
                rightButtonLabel="Confirm"
                handleClose={() => setConfirmTranscriptionRequest(false)}
                type="confirm"
              />
            )}
            <SubmitSummary />
            <div className="flex justify-between">
              <Button
                variant="outline"
                color="blue"
                onClick={() => setCurrentStepIndex(currentStepIndex - 1)}
              >
                Previous
              </Button>
              <Button onClick={handleSubmit}>Submit</Button>
            </div>
          </>
        )
    }
  }

  return (
    <div className="min-h-96 mx-auto max-w-4xl overflow-hidden bg-white p-4">
      <UploadFormHeader />
      <div className="space-y-12">
        {!hasUploadIssue && <div className="pb-12">{getStep()}</div>}
        {hasUploadIssue && (
          <div className="mb-5">
            <AlertMessage
              type="warning"
              message="We are currently experiencing issues with all uploads and have disabled submissions until we resolve the issue. Please check back later as we are working on a fix. Sorry for any inconveniences that this may be causing."
            />
          </div>
        )}
      </div>
    </div>
  )
}
